// ============================================================
// TypeScript interfaces matching SQL schema exactly
// ============================================================

// --- Common ---
export type UUID = string;

// --- Referral Program (M1) ---
export type VehicleCategory =
  | 'moto' | 'sedan' | 'small_van' | 'large_van'
  | 'xl_van' | 'estacas' | 'rabon' | 'torton' | 'trailer';

export const VEHICLE_CATEGORY_LABELS: Record<VehicleCategory, string> = {
  moto: 'Moto',
  sedan: 'Sedán',
  small_van: 'Van Pequeña',
  large_van: 'Van Grande',
  xl_van: 'Van XL',
  estacas: 'Estacas',
  rabon: 'Rabón',
  torton: 'Tortón',
  trailer: 'Tráiler',
};

export interface ReferralTier {
  id: UUID;
  vehicle_category: VehicleCategory;
  reward_amount: number;
  min_completed_routes: number;
  terms: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export type ReferralStatus = 'pending' | 'contacted' | 'onboarding' | 'activated' | 'rewarded' | 'expired' | 'rejected';

export interface Referral {
  id: UUID;
  referrer_driver_id: UUID;
  referred_name: string;
  referred_phone: string;
  referred_vehicle_category: VehicleCategory | null;
  referral_code: string;
  status: ReferralStatus;
  reward_tier_id: UUID | null;
  reward_amount: number | null;
  reward_paid_at: string | null;
  referred_driver_id: UUID | null;
  routes_completed: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export type ReferralEventType =
  | 'submitted' | 'contacted' | 'onboarding_started'
  | 'activated' | 'route_completed' | 'reward_triggered' | 'reward_paid';

export interface ReferralEvent {
  id: UUID;
  referral_id: UUID;
  event_type: ReferralEventType;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

// --- Profile & Onboarding (M2) ---
export type OnboardingStep = 'register' | 'verify_phone' | 'basic_info' | 'vehicle' | 'documents' | 'review' | 'active';
export type DriverType = 'independiente' | 'flotillero';
export type ShiftType = 'morning' | 'afternoon' | 'night' | 'full_day';

export const SHIFT_LABELS: Record<ShiftType, string> = {
  morning: 'Mañana (6-14h)',
  afternoon: 'Tarde (14-22h)',
  night: 'Noche (22-6h)',
  full_day: 'Día Completo',
};

export const ONBOARDING_STEPS: OnboardingStep[] = [
  'register', 'verify_phone', 'basic_info', 'vehicle', 'documents', 'review', 'active',
];

export interface DriverProfile {
  id: UUID;
  auth_user_id: UUID;
  core_driver_id: UUID | null;
  phone: string;
  display_name: string | null;
  profile_photo_url: string | null;
  bio: string | null;
  years_experience: number | null;
  preferred_zones: string[];
  preferred_shifts: string[];
  speaks_english: boolean;
  has_helpers: boolean;
  helper_count: number;
  certifications: string[];
  driver_type: DriverType;
  fleet_id: UUID | null;
  onboarding_step: OnboardingStep;
  onboarding_completed_at: string | null;
  rating: number;
  total_routes_completed: number;
  total_referrals: number;
  gamification_points: number;
  badge_ids: UUID[];
  current_streak_days: number;
  longest_streak_days: number;
  last_active_date: string | null;
  created_at: string;
  updated_at: string;
}

// --- Availability Calendar (M3) ---
export type SlotStatus = 'available' | 'tentative' | 'booked' | 'cancelled';

export interface AvailabilitySlot {
  id: UUID;
  driver_profile_id: UUID;
  vehicle_id: UUID | null;
  date: string;
  shift: ShiftType;
  zone_id: UUID | null;
  zone_name: string | null;
  status: SlotStatus;
  booked_service_id: UUID | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface AvailabilityTemplate {
  id: UUID;
  driver_profile_id: UUID;
  vehicle_id: UUID | null;
  day_of_week: number;
  shift: ShiftType;
  zone_id: UUID | null;
  zone_name: string | null;
  is_active: boolean;
  created_at: string;
}

// --- Vehicle Base Location (M4) ---
export interface VehicleLocation {
  id: UUID;
  driver_profile_id: UUID | null;
  vehicle_id: UUID;
  label: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  latitude: number | null;
  longitude: number | null;
  is_primary: boolean;
  last_confirmed_at: string | null;
  created_at: string;
  updated_at: string;
}

// --- Document Management (M5) ---
export type DocAppliesTo = 'driver' | 'vehicle' | 'both';
export type DocStatus = 'pending' | 'validating' | 'approved' | 'rejected' | 'expired';

export interface DocumentType {
  id: UUID;
  code: string;
  name: string;
  description: string | null;
  applies_to: DocAppliesTo;
  is_required: boolean;
  has_expiration: boolean;
  renewal_reminder_days: number;
  is_active: boolean;
}

export interface DriverDocument {
  id: UUID;
  driver_profile_id: UUID | null;
  vehicle_id: UUID | null;
  document_type_id: UUID;
  file_url: string;
  file_name: string | null;
  file_size: number | null;
  mime_type: string | null;
  status: DocStatus;
  docval_result: Record<string, unknown> | null;
  docval_confidence: number | null;
  issue_date: string | null;
  expiration_date: string | null;
  rejection_reason: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  version: number;
  created_at: string;
  updated_at: string;
  document_type?: DocumentType;
}

// --- Opportunities Feed (M6) ---
export type RouteType = 'dedicated' | 'on_demand' | 'seasonal';
export type OpportunityStatus = 'open' | 'filled' | 'closed' | 'expired';
export type OpportunityPriority = 'urgent' | 'high' | 'normal' | 'low';
export type ApplicationStatus = 'applied' | 'shortlisted' | 'interview' | 'offered' | 'accepted' | 'rejected' | 'withdrawn';

export interface Opportunity {
  id: UUID;
  solicitud_id: UUID | null;
  project_id: UUID | null;
  title: string;
  description: string | null;
  client_name: string | null;
  vehicle_category: VehicleCategory;
  zone_name: string | null;
  city: string | null;
  state: string | null;
  route_type: RouteType | null;
  estimated_pay_range: string | null;
  estimated_routes_per_week: number | null;
  requires_helpers: boolean;
  requires_fumigation: boolean;
  requires_refrigeration: boolean;
  start_date: string | null;
  duration: string | null;
  status: OpportunityStatus;
  priority: OpportunityPriority;
  slots_total: number;
  slots_filled: number;
  ai_generated_at: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface MatchFactors {
  vehicle_match: number;
  zone_match: number;
  availability: number;
  rating: number;
  documents: number;
}

export interface OpportunityApplication {
  id: UUID;
  opportunity_id: UUID;
  driver_profile_id: UUID;
  vehicle_id: UUID | null;
  match_score: number | null;
  match_factors: MatchFactors | null;
  status: ApplicationStatus;
  applied_at: string;
  notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  opportunity?: Opportunity;
}

// --- Gamification ---
export interface GamificationAction {
  id: UUID;
  action_code: string;
  name: string;
  points: number;
  description: string | null;
  is_active: boolean;
}

export interface GamificationLogEntry {
  id: UUID;
  driver_profile_id: UUID;
  action_code: string;
  points: number;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface Badge {
  id: UUID;
  code: string;
  name: string;
  description: string | null;
  icon_name: string;
  requirement: { action: string; count: number } | null;
  points_value: number;
  created_at: string;
}

// --- Leaderboard ---
export interface LeaderboardEntry {
  driver_profile_id: UUID;
  display_name: string | null;
  profile_photo_url: string | null;
  gamification_points: number;
  total_referrals: number;
  total_routes_completed: number;
  rank: number;
}

// --- Profile Completeness ---
export interface ProfileCompleteness {
  percentage: number;
  missing: string[];
}

export function calculateProfileCompleteness(profile: DriverProfile): ProfileCompleteness {
  const fields: { key: string; label: string; check: () => boolean }[] = [
    { key: 'display_name', label: 'Nombre', check: () => !!profile.display_name },
    { key: 'profile_photo_url', label: 'Foto de perfil', check: () => !!profile.profile_photo_url },
    { key: 'bio', label: 'Biografía', check: () => !!profile.bio },
    { key: 'years_experience', label: 'Años de experiencia', check: () => profile.years_experience !== null },
    { key: 'preferred_zones', label: 'Zonas preferidas', check: () => profile.preferred_zones.length > 0 },
    { key: 'preferred_shifts', label: 'Turnos preferidos', check: () => profile.preferred_shifts.length > 0 },
    { key: 'certifications', label: 'Certificaciones', check: () => profile.certifications.length > 0 },
  ];
  const completed = fields.filter(f => f.check());
  const missing = fields.filter(f => !f.check()).map(f => f.label);
  return {
    percentage: Math.round((completed.length / fields.length) * 100),
    missing,
  };
}
